import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import ModeSwitchDialog from '../components/ModeSwitchDialog.jsx'
import Modal from '../components/Modal.jsx'
import Icon from '../components/Icon.jsx'
import GlyphTile from '../components/GlyphTile.jsx'
import { modeTile } from '../lib/tileLook.js'
import { CardHead, SettingsRow } from '../components/SettingsLayout.jsx'
import { formatDate } from '../lib/dates.js'
import { useToast } from '../components/Toast.jsx'
import { ROLES } from '../lib/roles.js'
import { initialsOf } from '../lib/text.js'
import appIcon from '../assets/crewdispatch-service-icon.png'

/**
 * The settings landing page, and the home of the mode switch.
 *
 * The switch lives here rather than on its own page for the reason it is the only control in
 * the product that changes what every single person sees: burying the most consequential
 * action four rows down a sidebar, between "Job types" and "Wall displays", rated it the same
 * as a colour picker. First pane, first card, above everything else. What used to be the Mode
 * page keeps only the history table it was already showing — a timeline is reference material,
 * not an action, and it reads better once the switch is not sitting on top of it.
 *
 * Everyone gets this page, so it degrades by role rather than disappearing: an owner sees a
 * live switch, everybody else sees the same status read-only. Field staff still benefit from
 * knowing which board is running before they ask why the wall TV looks different.
 *
 * There is no shortcut grid down the bottom. It was the sidebar again, in tile form, one
 * scroll below the sidebar — a second copy of a list that is already permanently on screen.
 */

/** The live mode as a plain status line — what a non-owner gets in place of the switch. */
function ModeStatus({ current, modePhrase }) {
  const cat = current?.mode === 'cat'
  return (
    <div className={`mode-status ${cat ? 'cat' : 'normal'}`}>
      <GlyphTile {...modeTile(current?.mode)} size={44} />
      <span className="mode-text">
        <span className="mode-name">
          {modePhrase(current?.mode || 'normal')}
          {cat && current?.label ? ` · ${current.label}` : ''}
        </span>
        <span className="mode-detail">
          {current?.effectiveFrom
            ? `Running since ${formatDate(current.effectiveFrom, { withYear: true })}`
            : 'The board everyone is working from today.'}
        </span>
      </span>
      <span className="mode-live-pill">Live</span>
    </div>
  )
}

/** The former Account page's content, now a modal so the Overview stays the one screen
 *  someone lands on rather than sending them off to a settings tab for a quick glance. */
function AccountModal({ onClose }) {
  const { user, tenant, signOut, roleLabel } = useAuth()
  return (
    <Modal title="Account" onClose={onClose} width={420}>
      <div className="settings-list">
        <SettingsRow label="Name"><span>{user?.name}</span></SettingsRow>
        <SettingsRow label="Email"><span>{user?.email}</span></SettingsRow>
        <SettingsRow label="Company"><span>{tenant?.name}</span></SettingsRow>
        <SettingsRow label="Roles">
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            {/* Every role here is one the person actually holds, owner included — none of
                them is "muted", and owner gets no different a badge than the rest. Canonical
                order, since array_agg makes no promise about the order roles come back in. */}
            {ROLES.filter((r) => (user?.roles || []).includes(r)).map((r) => (
              <span key={r} className="tag">{roleLabel(r)}</span>
            ))}
          </div>
        </SettingsRow>
      </div>
      <div className="modal-actions" style={{ marginTop: 20 }}>
        <button className="btn danger" onClick={signOut}>Sign out</button>
      </div>
    </Modal>
  )
}

export default function Overview() {
  const { user, tenant, mode, can, pendingSwitch, refresh, modes, modeName, modePhrase, enabledModes } =
    useAuth()
  // Only what this company actually uses, named the way it names it. A mode turned off
  // under Configure is not something anyone should be offered a switch to.
  const modeCards = enabledModes().map((k) => ({
    key: k,
    name: modeName(k),
    detail: modes?.[k]?.description || '',
  }))
  const qc = useQueryClient()
  const toast = useToast()
  const [dialog, setDialog] = useState(null)
  const [accountOpen, setAccountOpen] = useState(false)

  const isOwner = Boolean(can.isOwner)

  // Only an owner can read the mode timeline, so everyone else is served from the session
  // payload they already have. Same three fields either way — the panel never branches on
  // where they came from.
  const { data } = useQuery({
    queryKey: ['mode-periods'],
    queryFn: () => api.get('/api/owner/mode-periods'),
    enabled: isOwner,
  })
  const { data: cat } = useQuery({
    queryKey: ['cat-settings'],
    queryFn: () => api.get('/api/owner/cat-settings'),
    enabled: isOwner,
  })

  const current = isOwner
    ? data?.current
    : { mode: mode?.mode, label: mode?.label, effectiveFrom: mode?.effectiveFrom }
  const pending = isOwner ? data?.pending : pendingSwitch

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['mode-periods'] })
    qc.invalidateQueries({ queryKey: ['schedule'] })
    refresh()
  }

  async function cancelPending(id) {
    try {
      await api.del(`/api/owner/mode-periods/${id}`)
      refreshAll()
    } catch (err) {
      toast.error(err)
    }
  }

  return (
    <>
      {/* This is the one settings pane that opens on a person's very first click, and the
          only screen in the product that isn't already standing under the tenant's own
          brand colour in the topbar — so it is where CrewDispatch Service gets to say which product
          this actually is, once, quietly, rather than on every pane. */}
      <div className="overview-brand">
        <img src={appIcon} alt="" className="overview-brand-mark" />
        <span className="overview-brand-name">CrewDispatch Service</span>
      </div>

      {/* Who you are, before what the company is doing. This pane is the first thing most
          people open, and "am I signed in as the right person, at the right company" is the
          question they can answer at a glance — the mode below is the one they came to read. */}
      <div className="card">
        <CardHead
          title="You"
          sub={`Signed in to ${tenant?.name || 'this company'}.`}
        />
        <div className="overview-you">
          <span className="you-avatar" aria-hidden="true">
            {initialsOf(user?.name) || '?'}
          </span>
          <span className="you-text">
            <strong>{user?.name}</strong>
            <span className="sub">{user?.email}</span>
          </span>
          <button className="btn" onClick={() => setAccountOpen(true)}>
            Account
          </button>
        </div>
      </div>

      <div className="card">
        <CardHead
          title="Operating mode"
          // Nothing for an owner: the controls below say what switching does, and an owner
          // reading a paragraph about their own authority learns nothing from it. Somebody
          // who CANNOT switch still needs telling why the card is inert.
          sub={isOwner ? undefined : 'Which board the whole company is running. Only an owner can change it.'}
        />

        {!current ? (
          <div className="empty">Loading…</div>
        ) : isOwner ? (
          <div className="mode-cards">
            {modeCards.map((m) => {
              const live = current.mode === m.key
              return (
                <button
                  key={m.key}
                  type="button"
                  className={`mode-card ${m.key} ${live ? 'live' : ''}`}
                  disabled={live}
                  aria-current={live ? 'true' : undefined}
                  onClick={() => !live && setDialog(m.key)}
                >
                  <GlyphTile {...modeTile(m.key)} size={44} />
                  <span className="mode-text">
                    <span className="mode-name">
                      {m.name}
                      {live && m.key === 'cat' && current.label ? ` · ${current.label}` : ''}
                      {live && <span className="mode-live-pill">Live</span>}
                    </span>
                    <span className="mode-detail">
                      {live && current.effectiveFrom
                        ? `Running since ${formatDate(current.effectiveFrom, { withYear: true })}`
                        : m.detail}
                    </span>
                  </span>
                  {/* The affordance is a chevron that slides, not the word "Switch" competing
                      with the mode's own name for the same eye. */}
                  {!live && (
                    <span className="mode-go" aria-hidden="true">
                      <Icon name="right" size={16} strokeWidth={2.2} />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        ) : (
          <ModeStatus current={current} modePhrase={modePhrase} />
        )}

        {pending && (
          <div className="notice mode-pending">
            {/* A bare glyph, not a tile — it is one line of notice, not a landmark. Still
                the mode's own glyph, from the same table, so it matches the card above. */}
            {(() => { const Glyph = modeTile(pending.mode).icon; return <Glyph size={16} strokeWidth={2} /> })()}
            <span>
              <strong>{modePhrase(pending.mode)}</strong> starts{' '}
              {formatDate(pending.effectiveFrom, { withYear: true })}
              {pending.label ? ` — ${pending.label}` : ''}.
            </span>
            {isOwner && (
              <button className="btn sm" onClick={() => cancelPending(pending.id)}>
                Cancel it
              </button>
            )}
          </div>
        )}
      </div>

      {dialog && (
        <ModeSwitchDialog
          target={dialog}
          today={mode?.today}
          defaultLabel={cat?.defaultLabel}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null)
            refreshAll()
          }}
        />
      )}

      {accountOpen && <AccountModal onClose={() => setAccountOpen(false)} />}
    </>
  )
}
